import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Voucher, VoucherType, VoucherStatus } from '../mocks/vouchers';

const dbTypeToMock = (t: string): VoucherType =>
  (['Hotel','Transfer','Tour','Flight','Activity','Cruise'].includes(t) ? t : 'Hotel') as VoucherType;

const dbStatusToMock = (s: string): VoucherStatus =>
  (['Active','Used','Cancelled','Expired'].includes(s) ? s : 'Active') as VoucherStatus;

function rowToVoucher(r: Record<string, unknown>): Voucher {
  return {
    id: r.id as string,
    number: (r.voucher_number as string) || '',
    bookingId: (r.booking_id as string) || '',
    type: dbTypeToMock((r.type as string) || 'Hotel'),
    status: dbStatusToMock((r.status as string) || 'Active'),
    customer: '',
    customerAvatar: 'CU',
    customerEmail: '',
    customerPhone: '',
    pax: 1,
    travelDate: (r.check_in as string) || '',
    endDate: (r.check_out as string) || '',
    issuedDate: (r.created_at as string) || '',
    destination: '',
    agent: 'You',
    supplierName: '',
    supplierAddress: '',
    supplierPhone: '',
    supplierEmail: '',
    supplierRef: '',
    serviceTitle: (r.service_name as string) || '',
    serviceDetails: [],
    checkIn: (r.check_in as string) || '',
    checkOut: (r.check_out as string) || '',
    confirmationCode: '',
    specialInstructions: (r.notes as string) || '',
  };
}

export function useVouchers() {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('vouchers').select('*').order('created_at', { ascending: false });
    setVouchers((data || []).map((r) => rowToVoucher(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchVouchers(); }, [fetchVouchers]);

  const createVoucher = async (voucher: Partial<Voucher>) => {
    const num = `VCH-${Date.now().toString().slice(-6)}`;
    const { data, error } = await supabase.from('vouchers').insert({
      voucher_number: num,
      type: voucher.type || 'Hotel',
      status: (voucher.status || 'Active').toLowerCase() === 'active' ? 'active' : voucher.status?.toLowerCase(),
      service_name: voucher.serviceTitle,
      check_in: voucher.checkIn || null,
      check_out: voucher.checkOut || null,
      notes: voucher.specialInstructions,
    }).select().maybeSingle();
    if (!error && data) setVouchers((prev) => [rowToVoucher(data as Record<string, unknown>), ...prev]);
    return { data, error };
  };

  const deleteVoucher = async (id: string) => {
    const { error } = await supabase.from('vouchers').delete().eq('id', id);
    if (!error) setVouchers((prev) => prev.filter((v) => v.id !== id));
  };

  return { vouchers, loading, refetch: fetchVouchers, createVoucher, deleteVoucher };
}
